import { FC } from 'react';
import { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export interface ToolCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  benefits: string[];
  path: string;
  color?: string;
  status?: 'active' | 'coming-soon' | 'beta';
}

const ToolCard: FC<ToolCardProps> = ({ 
  icon: Icon, 
  title, 
  description, 
  path, 
  color = "blue",
  status = 'active'
}) => {
  // Map colors to actual Tailwind classes to avoid string interpolation issues
  const getColor = (type: string) => {
    const colorMap: Record<string, Record<string, string>> = {
      blue: {
        bg: "bg-blue-50",
        iconBg: "bg-blue-100",
        text: "text-blue-600",
      },
      emerald: {
        bg: "bg-emerald-50",
        iconBg: "bg-emerald-100",
        text: "text-emerald-600",
      },
      purple: {
        bg: "bg-purple-50",
        iconBg: "bg-purple-100",
        text: "text-purple-600",
      },
      amber: {
        bg: "bg-amber-50",
        iconBg: "bg-amber-100",
        text: "text-amber-600",
      }
    };
    
    return colorMap[color] || colorMap.blue;
  };
  
  const colors = getColor(color);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Link 
        to={path} 
        target={status === 'active' ? '_blank' : undefined}
        rel={status === 'active' ? 'noopener noreferrer' : undefined}
        className={`block ${status === 'active' ? '' : 'pointer-events-none opacity-70'}`}
      >
        <div className={`flex items-center p-4 rounded-xl ${status === 'active' ? 'hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer' : ''} transition-colors group`}>
          {/* Icon container - Apple style rounded square icon */}
          <div className={`flex-shrink-0 w-14 h-14 rounded-xl ${colors.iconBg} ${colors.text} flex items-center justify-center mr-4 shadow-sm`}>
            <Icon className="h-8 w-8" />
          </div>
          
          {/* Text content */}
          <div className="flex-grow">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white pb-0">{title}</h3>
              
              {/* Status badge or get button */}
              {status === 'coming-soon' ? (
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  Coming Soon
                </span>
              ) : status === 'beta' ? (
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                  Beta
                </span>
              ) : (
                <span className="text-sm font-medium px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/40 transition-all group-hover:bg-blue-100 dark:group-hover:bg-blue-800/30 group-hover:shadow-sm">
                  Try Now
                </span>
              )}
            </div>
            
            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
              {description}
            </p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ToolCard;